import React from 'react';
import { useMenuPermissions } from '../../hooks/useMenuPermissions';

export const UserRoleInfo: React.FC = () => {
  const { getUserRole, isAdmin, getAllowedMenus } = useMenuPermissions();

  const userRole = getUserRole();
  const userIsAdmin = isAdmin();
  const allowedMenus = getAllowedMenus();

  const getRoleDisplayName = (role: string): string => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'Administrador';
      case 'manager':
        return 'Gerente';
      case 'editor':
        return 'Editor';
      case 'viewer':
        return 'Visualizador';
      case 'user':
        return 'Visitante';
      default:
        return role || 'Usuário';
    }
  };

  const getRoleDescription = (role: string): string => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'Acesso completo a todas as funcionalidades do sistema';
      case 'manager':
        return 'Pode gerenciar documentos, equipes, perfis e permissões';
      case 'editor':
        return 'Pode criar e editar documentos e modelos';
      case 'viewer':
        return 'Pode visualizar documentos, ações de pessoas da equipe e usar o chat';
      case 'user':
        return 'Acesso apenas ao chat e notificações';
      default:
        return 'Acesso limitado ao sistema';
    }
  };

  const getRoleColor = (role: string): string => {
    // Retorna sempre a mesma cor neutra para todos os perfis
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Informações do Usuário</h3>
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRoleColor(userRole)}`}>
          {getRoleDisplayName(userRole)}
        </span>
      </div>

      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Descrição da Função</h4>
          <p className="text-sm text-gray-600">{getRoleDescription(userRole)}</p>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Módulos Acessíveis</h4>
          <div className="grid grid-cols-2 gap-2">
            {allowedMenus.map(menu => (
              <div key={menu.path} className="flex items-center text-sm text-gray-600">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {menu.label}
              </div>
            ))}
          </div>
        </div>

        {userIsAdmin && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <div className="flex">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-yellow-400 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-yellow-800">Acesso Administrativo</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Você tem acesso completo a todas as funcionalidades do sistema.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 